import {useCallback, useEffect, useRef} from 'react';

type Event = MouseEvent | TouchEvent | KeyboardEvent;

const useOnClickOutside = <T = Record<string, HTMLElement>>(handler: (event: Event) => void, useClickEvent: boolean) => {
	const ref = useRef<T>(null);

	const clickListener = useCallback(
		(event: Event) => {
			const refsList = Object.values(ref.current || {}) as Array<T[keyof T]>;
			const refsListLen = refsList.length;
			let isInside = false;

			for (let i = 0; i < refsListLen; i += 1) {
				const ref = refsList[i] as HTMLElement;

				if (!ref || ref.contains((event?.target as Node) || null)) {
					isInside = true;

					break;
				}
			}

			if (!isInside) {
				handler(event); // Call the handler only if the click is outside of the elements passed.
			}
		},
		[ref],
	);

	const escapeListener = useCallback((event: KeyboardEvent) => {
		if (event.key === 'Escape') {
			handler(event);
		}
	}, []);

	useEffect(() => {
		if (useClickEvent) {
			document.addEventListener('click', clickListener);
		} else {
			document.addEventListener('mousedown', clickListener);
			document.addEventListener('touchstart', clickListener);
		}

		document.addEventListener('keyup', escapeListener);

		return () => {
			if (useClickEvent) {
				document.removeEventListener('click', clickListener);
			} else {
				document.removeEventListener('mousedown', clickListener);
				document.removeEventListener('touchstart', clickListener);
			}

			document.removeEventListener('keyup', escapeListener);
		};
	}, [ref, handler]); // Reload only if ref or handler changes

	return ref;
};

export default useOnClickOutside;
